import { gql } from 'graphql-request';
import { useContext, Show, For, type JSX } from 'solid-js';
import { Link } from '@solidjs/router';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { Grid } from '../../grid-system/grid/grid';
import { NewAtosLogoWhite } from '../icons-library/new-atos-logo-white';
import { LinkedinIconPlain } from '../icons-library/social-media/linkedin-plain';
import { InstagramIcon } from '../icons-library/social-media/instagram';
import { YoutubePlainIcon } from '../icons-library/social-media/youtube-plain';
import { FacebookIconPlain } from '../icons-library/social-media/facebook-plain';
import { TwitterIconPlain } from '../icons-library/social-media/twitter-plain';
import { ChevronDownIcon } from '../icons-library/chevron-down';
import { ErrorCatcher } from '../../tools/error-catcher';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';

const GET_FOOTER_QUERY = gql`
    query GetFooter($key: String!) {
        footer: resource(key: $key) {
            content
            key
        }
    }
`;

type ColumnItem = {
    title: string;
    url: string;
    target: string;
}
type FooterColumn = {
    header: string;
    columnItems: ColumnItem[]
};

const getColumnTemplate = (count: number): number[] => {
    if (!count) return [12];
    const colSpan = Math.floor(12 / count);
    const remainder = 12 % count;
    return Array.from({ length: count }, (_, i) => colSpan + (i < remainder ? 1 : 0));
};

const getSocialIcon = (url?: string): JSX.Element | null => {
    if (!url) return null;
    const lowered = url.toLowerCase();
    if (lowered.includes('linkedin.com')) return <LinkedinIconPlain size="lg" fill="currentColor" />;
    if (lowered.includes('youtube.com') || lowered.includes('youtu.be')) return <YoutubePlainIcon size="lg" fill="currentColor" />;
    if (lowered.includes('instagram.com')) return <InstagramIcon size="lg" fill="currentColor" />;
    if (lowered.includes('facebook.com') || lowered.includes('fb.com')) return <FacebookIconPlain size="lg" fill="currentColor" />;
    if (lowered.includes('twitter.com') || lowered.includes('x.com')) return <TwitterIconPlain size="lg" fill="currentColor" />;
    return null;
};

export const FooterContainer = () => {
    const { createCachedResource, localize } = useContext(AppContext);
    const [footerContent] = createCachedResource(GET_FOOTER_QUERY, () => ({ key: '/wp-json/rawb/v1/footer' }), true);

    const parsedCopyrightText = () => {
        const text = footerContent()?.footer?.content?.copyrighttext;
        if (!text) return '';
        const match = text.match(/\{\{(.*?)\}\}/);
        if (!match) return text;
        const currentYear = new Date().getFullYear();
        return text.replace(match[0], currentYear.toString());
    };

    const handleOnClickCookieConsent = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.CookieConsent.renew();
    };

    const renderLinkRow = (columnItem: ColumnItem) => {
        const icon = getSocialIcon(columnItem.url);
        return (
            <Link
                href={removeTrailingSlash(columnItem.url)}
                target={columnItem.target}
                class="group/link flex gap-cvi-5 items-start py-cvi-2 w-full text-cvi-fg-on-color"
            >
                <Show when={icon}>
                    <span class="flex items-center justify-center size-cvi-icon-box shrink-0">
                        {icon}
                    </span>
                </Show>
                <span class="body-md font-cvi-regular pt-cvi-1 flex-1 min-w-0 group-hover/link:underline">
                    {columnItem.title}
                </span>
            </Link>
        );
    };

    const handleDetailsKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') {
            return;
        }
        const details = e.currentTarget as HTMLDetailsElement;
        if (!details?.open) {
            return;
        }
        details.open = false;
        (details.querySelector('summary') as HTMLElement | null)?.focus();
    };

    const renderCookieConsentButton = () => (
        <button
            type="button"
            onClick={handleOnClickCookieConsent}
            class="body-xs font-cvi-regular text-cvi-fg-on-color-muted bg-transparent border-0 p-0 cursor-pointer hover:underline"
        >
            {localize('cookie-consent', 'Cookie consent')}
        </button>
    );

    return (
        <ErrorCatcher componentName='Footer'>
            <footer class="[container-type:inline-size] bg-[var(--component-footer-default-bg-color)] text-cvi-fg-on-color print:hidden">
                <Section
                    templateShorthand={[12]}
                    widthType="bgFull"
                    removeSpacingBlock={true}
                    customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                >
                    <div class="flex flex-col gap-cvi-9 py-cvi-10 desktop-small:gap-cvi-13 desktop-small:pt-cvi-15 desktop-small:pb-cvi-13 w-full">
                        <div>
                            {/* Compact logo on mobile/tablet.*/}
                            <div class="block desktop-small:hidden w-cvi-logo-sm-w h-cvi-logo-sm-h">
                                <NewAtosLogoWhite width="100%" height="100%" />
                            </div>
                            <div class="hidden desktop-small:block">
                                <NewAtosLogoWhite />
                            </div>
                        </div>

                        <Show when={footerContent()?.footer?.content?.columns}>
                            {/* Mobile / tablet: accordion — each WP column a <details> */}
                            <div class="flex flex-col w-full desktop-small:hidden">
                                <For each={footerContent().footer.content.columns}>
                                    {(column: FooterColumn) => {
                                        if (!column) return null;
                                        return (
                                            <details class="group w-full border-b border-cvi-border-on-color" onKeyDown={handleDetailsKeyDown}>
                                                <summary class="flex items-start gap-cvi-5 py-cvi-6 px-cvi-4 cursor-pointer list-none box-border [&::-webkit-details-marker]:hidden">
                                                    <h2 class="heading-lg font-cvi-bold text-cvi-fg-on-color flex-1 min-w-0 pt-cvi-1 m-0">
                                                        {column.header}
                                                    </h2>
                                                    <span class="flex items-center justify-center size-cvi-icon-box shrink-0 transition-transform duration-200 group-open:rotate-180 text-cvi-fg-on-color">
                                                        <ChevronDownIcon />
                                                    </span>
                                                </summary>
                                                <div class="flex flex-col items-start pb-cvi-6 px-cvi-4">
                                                    <For each={column.columnItems}>
                                                        {(columnItem) => (
                                                            <Show when={columnItem?.title && columnItem?.url}>
                                                                {renderLinkRow(columnItem)}
                                                            </Show>
                                                        )}
                                                    </For>
                                                </div>
                                            </details>
                                        );
                                    }}
                                </For>
                            </div>

                            {/* Desktop: columns placed in the page's 12-col grid */}
                            <div class="hidden desktop-small:block w-full">
                                <Grid templateShorthand={getColumnTemplate(footerContent().footer.content.columns.length)}>
                                    <For each={footerContent().footer.content.columns}>
                                        {(column: FooterColumn) => {
                                            if (!column) return null;
                                            return (
                                                <div class="flex flex-col items-start min-w-0">
                                                    <div class="pb-cvi-6 w-full">
                                                        <p class="heading-lg font-cvi-medium text-cvi-fg-on-color">
                                                            {column.header}
                                                        </p>
                                                    </div>
                                                    <div class="flex flex-col items-start w-full">
                                                        <For each={column.columnItems}>
                                                            {(columnItem) => (
                                                                <Show when={columnItem?.title && columnItem?.url}>
                                                                    {renderLinkRow(columnItem)}
                                                                </Show>
                                                            )}
                                                        </For>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    </For>
                                </Grid>
                            </div>
                        </Show>

                        <div class="flex flex-col gap-cvi-4">
                            <Show when={parsedCopyrightText()}>
                                <div class="flex flex-col gap-cvi-4 w-full">
                                    <p class="body-xs font-cvi-regular text-cvi-fg-on-color-muted">
                                        {`${localize('copyright', 'Copyright')} ${parsedCopyrightText()}`}
                                    </p>
                                </div>
                            </Show>
                            <div class="flex flex-wrap items-center gap-x-cvi-5 gap-y-cvi-2 w-full">
                                {renderCookieConsentButton()}
                            </div>
                        </div>
                    </div>
                </Section>
            </footer>
        </ErrorCatcher>
    );
};
